import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getCompanyDetails } from "api/dashboard/company";

interface CompanyState {
  companyDetails: any | null;
  subscription: any | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: CompanyState = {
  companyDetails: null,
  subscription: null,
  status: "idle",
  error: null,
};

// Async thunk strictly prevents multiple simultaneous fetches natively via 'condition'
export const fetchCompanyProfile = createAsyncThunk(
  "company/fetchCompanyProfile",
  async (companyId: string, { rejectWithValue }) => {
    try {
      const response = await getCompanyDetails(companyId);
      if (!response || response.length === 0 || !response[0]) {
        return rejectWithValue("Company not found");
      }
      return response[0];
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch company details");
    }
  },
  {
    condition: (companyId, { getState }: any) => {
      const { companyReducer } = getState();
      if (companyReducer?.status === "loading") {
        return false;
      }
      if (companyReducer?.status === "succeeded" && companyReducer?.companyDetails?._id === companyId) {
        return false;
      }
      return true;
    },
  }
);

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    invalidateCompanyProfile: (state) => {
      state.status = "idle";
      state.companyDetails = null;
      state.subscription = null;
    },
    updateCompanyPublishStatus: (state, action: PayloadAction<boolean>) => {
      if (state.companyDetails) {
        state.companyDetails.published = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyProfile.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCompanyProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.companyDetails = action.payload.companyDetails;
        state.companyDetails.published = action.payload.published || false; // Merge top level publish status
        // Extract extra top-level fields
        state.companyDetails.about = action.payload.about;
        state.companyDetails.mission = action.payload.mission;
        state.companyDetails.vision = action.payload.vision;
        state.companyDetails.profileImage = action.payload.profileImage;
        state.companyDetails.no_of_employees = action.payload.no_of_employees;
        state.companyDetails.industry = action.payload.industry;
        state.companyDetails.subdomainSlug = action.payload.subdomainSlug;
        
        state.subscription = action.payload.subscription;
      })
      .addCase(fetchCompanyProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase("user/addUser", (state, action: any) => {
        // Clear company state on user logout (when addUser is called without a valid session payload)
        if (!action.payload || Object.keys(action.payload).length === 0) {
          state.status = "idle";
          state.companyDetails = null;
          state.subscription = null;
          state.error = null;
        }
      })
      .addCase("user/clearAuthenticatedUser", (state) => {
        // Explicit cleanup on lifecycle-based logout
        state.status = "idle";
        state.companyDetails = null;
        state.subscription = null;
        state.error = null;
      });
  },
});

export const { invalidateCompanyProfile, updateCompanyPublishStatus } = companySlice.actions;

export default companySlice.reducer;
